import { useLocation, Outlet, Link } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  LogOut,
  Home,
  Users,
  Server,
  User,
  Wrench,
  FileText,
  Menu,
  X,
  ChevronRight,
  Moon,
  Sun,
  Settings,
  Shield,
  ScrollText,
  AlertTriangle,
} from 'lucide-react';
import { Toaster } from '@/components/ui/toaster';
import { OktaLogo } from '@/components/okta-logo';
import {
  getServiceAppStatus,
  provisionServiceApp,
  type ServiceAppProvisionResponse,
} from '@/lib/api/service-app';
import { useFeatureFlags } from '@/lib/feature-flags';

interface NavSection {
  title: string;
  items: {
    href: string;
    label: string;
    icon: any;
    featureFlag?: string;
  }[];
}

export const DashboardLayout = () => {
  const location = useLocation();
  const { oktaAuth, authState } = useOktaAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const { isEnabled } = useFeatureFlags();
  const [serviceAppBanner, setServiceAppBanner] = useState<{
    variant: 'success' | 'warning' | 'error';
    message: string;
  } | null>(null);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.toggle('dark', savedTheme === 'dark');
    }
  }, []);

  // Detect whether the Okta service app is configured; if not, attempt to
  // auto-provision it using this admin's own Okta token (requires
  // okta.apps.manage). Runs once per session — the backend is idempotent
  // (checks DB then env before creating anything), so this is a safe no-op
  // for deployments that already have a service app configured.
  useEffect(() => {
    getServiceAppStatus()
      .then((statusResult) => {
        if (statusResult.configured) return;
        return provisionServiceApp().then((result: ServiceAppProvisionResponse) => {
          if (result.status === 'already_configured') return;
          if (result.requires_manual_key_upload) {
            setServiceAppBanner({
              variant: 'warning',
              message: `Service app provisioned (client_id: ${result.client_id}), but jwks_uri (${result.jwks_uri}) is not reachable by Okta from a loopback host. Paste the adapter's public key manually, or set GATEWAY_BASE_URL to a public HTTPS host and re-provision. See Settings → Service App for details.`,
            });
          } else if (result.scopes_failed && result.scopes_failed.length > 0) {
            setServiceAppBanner({
              variant: 'warning',
              message: `Service app provisioned (client_id: ${result.client_id}), but these scopes need to be granted manually in the Okta Admin Console: ${result.scopes_failed.join(', ')}. It also needs an admin role assigned — recommended: AI Agent Administrator + Application Administrator (or a more restrictive custom role covering these scopes; Super Admin also works but grants more than needed). See Settings → Service App for details.`,
            });
          } else {
            setServiceAppBanner({
              variant: 'success',
              message: `Okta service app auto-provisioned (client_id: ${result.client_id}).`,
            });
          }
        });
      })
      .catch((err) => {
        setServiceAppBanner({
          variant: 'error',
          message: `Failed to auto-provision the Okta service app: ${err.message || err}. Configure it manually — see docs/OKTA_SERVICE_APP_SETUP.md, or Settings → Service App.`,
        });
      });
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const handleLogout = async () => {
    try {
      await oktaAuth.signOut();
      // Disable redirect to login page after logout, as the Okta signOut method will handle redirection
      // window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/login';
    }
  };

  const navSections: NavSection[] = [
    {
      title: 'Dashboard',
      items: [
        { href: '/dashboard', label: 'Overview', icon: Home },
      ],
    },
    {
      title: 'Configuration',
      items: [
        { href: '/dashboard/agents', label: 'Agents', icon: Users },
        { href: '/dashboard/resources', label: 'Resources', icon: Server },
        { href: '/dashboard/tools', label: 'Tools', icon: Wrench },
      ],
    },
    {
      title: 'Security & Compliance',
      items: [
        { href: '/dashboard/policies', label: 'Policies', icon: Shield, featureFlag: 'cedarEnabled' },
        { href: '/dashboard/audit', label: 'Audit Log', icon: FileText },
        { href: '/dashboard/event-log', label: 'Event Log', icon: ScrollText },
        { href: '/dashboard/sessions', label: 'User Sessions', icon: LogOut },
      ],
    },
    {
      title: 'Settings',
      items: [
        { href: '/dashboard/settings/dcr', label: 'DCR Policy', icon: Settings },
        { href: '/dashboard/settings/service-app', label: 'Service App', icon: Shield },
      ],
    },
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
        {!sidebarCollapsed && (
          <div className="flex items-center space-x-3">
            <OktaLogo className="h-8" isDark={theme === 'dark'} />
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Okta MCP Bridge</h3>
          </div>
        )}
        {sidebarCollapsed && (
          <div className="mx-auto">
            <OktaLogo className="h-8" isDark={theme === 'dark'} />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-6">
        {navSections.map((section) => (
          <div key={section.title}>
            {!sidebarCollapsed && (
              <h3 className="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                {section.title}
              </h3>
            )}
            <div className="space-y-1">
              {section.items.map((item) => {
                if (item.featureFlag && !isEnabled(item.featureFlag)) {
                  return null; // Skip rendering this item if the feature flag condition isn't met
                }
                const Icon = item.icon;
                const isActive = item.href === '/dashboard'
                  ? location.pathname === item.href
                  : location.pathname.startsWith(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary text-white'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    } ${sidebarCollapsed ? 'justify-center' : ''}`}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <Icon className={`w-5 h-5 shrink-0 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                    {!sidebarCollapsed && <span>{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t dark:border-gray-700 space-y-2">
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleTheme}
          className={`w-full ${sidebarCollapsed ? 'justify-center' : 'justify-start'}`}
          title="Toggle theme"
        >
          {theme === 'light' ? (
            <Moon className={`w-4 h-4 shrink-0 ${sidebarCollapsed ? '' : 'mr-2'}`} />
          ) : (
            <Sun className={`w-4 h-4 shrink-0 ${sidebarCollapsed ? '' : 'mr-2'}`} />
          )}
          {!sidebarCollapsed && <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>}
        </Button>

        {/* Collapse Toggle - Desktop only */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className={`hidden md:flex w-full ${sidebarCollapsed ? 'justify-center' : 'justify-start'}`}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronRight
            className={`w-4 h-4 shrink-0 transition-transform ${
              sidebarCollapsed ? '' : 'rotate-180 mr-2'
            }`}
          />
          {!sidebarCollapsed && <span>Collapse</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-white dark:bg-gray-800 border-r dark:border-gray-700 transition-all duration-300 ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        } ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      >
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div
        className={`transition-all duration-300 ${
          sidebarCollapsed ? 'md:pl-16' : 'md:pl-64'
        }`}
      >
        {/* Top bar */}
        <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-4">
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </Button>

              {/* Mobile logo */}
              <div className="flex items-center space-x-3 md:hidden">
                <OktaLogo className="h-7" isDark={theme === 'dark'} />
                <h3 className="text-base font-semibold text-gray-800 dark:text-white">Okta MCP Bridge</h3>
              </div>
            </div>

            {/* User menu */}
            <div className="flex items-center space-x-4">
              {authState?.isAuthenticated && (
                <div className="hidden sm:flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <User className="w-4 h-4 mr-2" />
                  <span>{authState.idToken?.claims.email || authState.idToken?.claims.name}</span>
                </div>
              )}
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto space-y-4">
            {serviceAppBanner && (
              <div
                className={`flex items-start gap-3 p-4 rounded-lg border text-sm ${
                  serviceAppBanner.variant === 'success'
                    ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 text-green-900 dark:text-green-200'
                    : serviceAppBanner.variant === 'warning'
                    ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200'
                    : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-900 dark:text-red-200'
                }`}
              >
                <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div className="flex-1">{serviceAppBanner.message}</div>
                <button
                  onClick={() => setServiceAppBanner(null)}
                  className="flex-shrink-0 opacity-60 hover:opacity-100"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>

      <Toaster />
    </div>
  );
};
